import React, { createContext, useContext, useState } from 'react';

// Редаговане поле профілю.
// Винесено з ProfilePage, щоб поле НЕ закривалося під час набору,
// коли сторінка оновлює баланси гаманця.
// Дані (genesisID, onSaved) приходять через InfoFieldContext.Provider.

const API = 'https://orkxz4jpah.execute-api.us-east-1.amazonaws.com/update-profile';

export const InfoFieldContext = createContext({ genesisID: null, onSaved: () => {} });

const calcAge = (dob) => {
  const d = new Date(dob);
  if (isNaN(d)) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
  return age >= 0 && age < 130 ? age : null;
};

function InfoField({ label, value, required = false, fieldName = null }) {
  const { genesisID, onSaved } = useContext(InfoFieldContext);
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const isDate = fieldName === 'dateOfBirth';

  const handleEdit = () => {
    setEditValue(value || '');
    setError('');
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditValue(value || '');
    setError('');
  };

  const handleSave = async () => {
    if (!fieldName || !genesisID || saving) return;
    const v = String(editValue).trim();
    setError('');

    if (!v) { setError(required ? 'This field is required' : 'Value cannot be empty'); return; }
    if (v.length > 2000) { setError('Too long'); return; }
    if (v === String(value || '').trim()) { setIsEditing(false); return; }

    const payload = { genesisID, [fieldName]: v };
    if (isDate) {
      const age = calcAge(v);
      if (age === null) { setError('Please enter a valid date'); return; }
      payload.age = age;
    }

    setSaving(true);
    try {
      const res = await fetch(API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const r = await res.json().catch(() => ({}));
      if (!r.success) throw new Error(r.error || 'Could not save. Please try again.');

      const { genesisID: _ignored, ...saved } = payload;
      onSaved(saved); // оновлюємо профіль ЛИШЕ після підтвердження сервера
      setIsEditing(false);
    } catch (e) {
      setError(e.message === 'Failed to fetch' ? 'Network error. Please try again.' : e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`info-item ${!value ? 'empty-field' : ''}`}>
      <span className="info-label">
        {label}
        {required && <span className="required-star">*</span>}
      </span>
      {isEditing ? (
        <div className="edit-field">
          <input
            type={isDate ? 'date' : 'text'}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') handleCancel(); }}
            className="edit-input"
            disabled={saving}
            autoFocus
          />
          {error && <span style={{ color: '#b42318', fontSize: '13px' }}>❌ {error}</span>}
          <div className="edit-actions">
            <button onClick={handleSave} disabled={saving} className="save-btn">
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button onClick={handleCancel} disabled={saving} className="cancel-button">
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="field-display">
          <span className={`info-value ${!value ? 'empty-value' : ''}`}>
            {value || 'Not specified'}
          </span>
          {fieldName && (
            <button onClick={handleEdit} className="edit-btn">
              Edit
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default InfoField;
